import { fetchApi } from '@/services/api-client';

export const INTEGRATION_PATH = '/admin/integration';

// Never includes the (encrypted) password itself — just whether one is set.
export type DoorDevice = {
  id: string;
  name: string;
  ipAddress: string;
  port: number;
  username: string;
  enabled: boolean;
  pollIntervalMinutes: number;
  hasPassword: boolean;
};

export type CreateDoorDeviceInput = {
  name: string;
  ipAddress: string;
  port?: number;
  username: string;
  password: string;
  enabled?: boolean;
  pollIntervalMinutes?: number;
};

// `password` is write-only and optional here: omit it to leave the stored password unchanged.
export type UpdateDoorDeviceInput = Partial<Omit<CreateDoorDeviceInput, 'password'>> & {
  password?: string;
};

export type IntegrationSettings = {
  bellEnabled: boolean;
  bellVolume: number;
  // The selected bell recording id (filename stem, e.g. 'bell-classic-loop').
  bellSound: string;
  anthemEnabled: boolean;
  // Bare "HH:MM" clock string (school-local time), not an ISO timestamp.
  anthemTime: string;
  anthemSound: string;
  lastBellEnabled: boolean;
  lastBellSound: string;
  breakWarningEnabled: boolean;
  breakWarningOffsetMinutes: number;
  breakWarningSound: string;
  // Attendance-from-door-swipes config — see AttendanceDaily's auto check-in/out logic.
  // Bare "HH:MM" clock string (school-local time), not an ISO timestamp.
  checkoutEagerHour: string;
  checkoutQuietMinutes: number;
  // Comma-separated door event minor codes that count as a granted (successful) swipe.
  accessGrantedMinorCodes: string;
  // The last on-time minute for a first door tap, "HH:MM": at or before it the day is marked
  // present, after it late. Whole minutes, so 08:00:55 is still on time.
  lateAfterStaff: string;
  lateAfterStudents: string;
};

export type UpdateIntegrationSettingsInput = Partial<IntegrationSettings>;

// ---- Agent status ----------------------------------------------------------

export type BellEvent = {
  // Bare "HH:MM" clock string (school-local time), not an ISO timestamp.
  time: string;
  kind: 'anthem' | 'bell' | 'break-warning' | 'last-bell';
  sound: string;
};

export type DeviceStatus = {
  id: string;
  name: string;
  ipAddress: string;
  port: number;
  pollIntervalMinutes: number;
  enabled: boolean;
  lastEventAt: string | null;
  lastPollAt: string | null;
  lastPollOk: boolean | null;
  lastPollError: string | null;
};

// Why the bell schedule is/isn't currently active — drives the dashboard widget's tone/copy.
export type BellReason = 'active' | 'disabled' | 'no_active_year' | 'not_school_day' | 'no_schedule';

export type AgentStatus = {
  lastSeenAt: string | null;
  // Per-interface string, e.g. "eth0 192.168.100.63 · wlan0 192.168.100.201" — the Pi may be
  // on wired + Wi-Fi at once. Render verbatim; do not parse.
  agentIp: string | null;
  agentVersion: string | null;
  // Pi's most recent self-reported bell-audio-hardware check. null until the agent has
  // reported at least once.
  audioOk: boolean | null;
  audioError: string | null;
  // The most-recently-passed and next-upcoming bell events for today, or null on a
  // non-school day / once past the last event.
  lastBell: BellEvent | null;
  nextBell: BellEvent | null;
  bellActive: boolean;
  bellReason: BellReason;
  // Non-null when today's resolved schedule contains a sound that would still be playing when
  // the next event is due — set by AgentService.computeBellStatus. Save-time validation blocks
  // this at settings-save time, but editing periods or attaching a schedule variant can
  // invalidate an already-saved configuration; this is how that becomes visible.
  scheduleWarning: string | null;
  devices: DeviceStatus[];
};

/** One ring on the bell day view. */
export type BellDayEvent = {
  time: string;
  kind: BellEvent['kind'];
  sound: string;
  // What starts at this time for each lunch group. The two differ only across the midday
  // swap, where one group eats while the other has a lesson; the anthem and break warnings
  // start no period at all, so both are null there.
  startsFirstGroup: string | null;
  startsSecondGroup: string | null;
};

/** Every ring for one date, exactly as the Pi would play them. */
export type BellDaySchedule = {
  date: string;
  isSchoolDay: boolean;
  bellEnabled: boolean;
  reason: BellReason;
  // Bell-schedule variant applied to this date, or null when it uses the regular periods.
  variantName: string | null;
  // Calendar-day label, e.g. "In-Service Day".
  dayLabel: string | null;
  scheduleWarning: string | null;
  events: BellDayEvent[];
};

export type ApiKeyResponse = {
  apiKey: string | null;
};

export type DoorAccessEvent = {
  id: string;
  deviceId: string;
  deviceName: string;
  serialNo: number;
  eventTime: string;
  cardNo: string | null;
  personName: string | null;
  employeeNoString: string | null;
  doorNo: number | null;
  cardReaderNo: number | null;
  minor: number | null;
  verifyMode: string | null;
  userId: string | null;
  userName: string | null;
  /** How the swipe was tied to a person; null when nobody could be identified. */
  matchMethod: DoorMatchMethod | null;
};

/** 'exact' is the panel sending a real personId; the rest were inferred from a wrong one. */
export type DoorMatchMethod = 'exact' | 'zeroPadded' | 'suffix';

export type UnmatchedSwipeGroup = {
  employeeNoString: string | null;
  personName: string | null;
  count: number;
  lastSeen: string;
};

export type UnmatchedSwipes = {
  groups: UnmatchedSwipeGroup[];
  total: number;
};

export type DoorAccessEventsListResponse = {
  items: DoorAccessEvent[];
  total: number;
  page: number;
  pageSize: number;
};

export type ListDoorAccessEventsFilters = {
  page?: number;
  deviceId?: string;
  date?: string;
  q?: string;
  linkedOnly?: boolean;
  // Exact match against a single user's id — the "select just one user" picker,
  // in addition to the fuzzy `q` person search above.
  userId?: string;
};

export const integrationApi = {
  // ---- Devices ----------------------------------------------------------
  listDevices: () => fetchApi<DoorDevice[]>(`${INTEGRATION_PATH}/devices`),

  createDevice: (data: CreateDoorDeviceInput) =>
    fetchApi<DoorDevice>(`${INTEGRATION_PATH}/devices`, { method: 'POST', body: JSON.stringify(data) }),

  updateDevice: (id: string, data: UpdateDoorDeviceInput) =>
    fetchApi<DoorDevice>(`${INTEGRATION_PATH}/devices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteDevice: (id: string) =>
    fetchApi<{ success: boolean }>(`${INTEGRATION_PATH}/devices/${id}`, { method: 'DELETE' }),

  // ---- Settings -----------------------------------------------------------
  getSettings: () => fetchApi<IntegrationSettings>(`${INTEGRATION_PATH}/settings`),

  updateSettings: (data: UpdateIntegrationSettingsInput) =>
    fetchApi<IntegrationSettings>(`${INTEGRATION_PATH}/settings`, { method: 'PATCH', body: JSON.stringify(data) }),

  // ---- API key --------------------------------------------------------
  revealApiKey: () => fetchApi<ApiKeyResponse>(`${INTEGRATION_PATH}/api-key`),

  regenerateApiKey: () => fetchApi<ApiKeyResponse>(`${INTEGRATION_PATH}/api-key`, { method: 'POST' }),

  // ---- Agent status -------------------------------------------------------
  getAgentStatus: () => fetchApi<AgentStatus>(`${INTEGRATION_PATH}/agent-status`),
  /** Who the door log can be filtered to: staff and students, since both carry cards. */
  searchPeople: (q: string) => fetchApi<DoorPerson[]>(`${INTEGRATION_PATH}/people?q=${encodeURIComponent(q)}`),

  getUnmatchedSwipes: () => fetchApi<UnmatchedSwipes>(`${INTEGRATION_PATH}/unmatched-swipes`),

  // ---- Bell day schedule ---------------------------------------------------
  getBellSchedule: (date: string) =>
    fetchApi<BellDaySchedule>(`${INTEGRATION_PATH}/bell-schedule?date=${encodeURIComponent(date)}`),

  // ---- Events -----------------------------------------------------------
  listEvents: (filters: ListDoorAccessEventsFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', String(filters.page));
    if (filters.deviceId) params.append('deviceId', filters.deviceId);
    if (filters.date) params.append('date', filters.date);
    if (filters.q) params.append('q', filters.q);
    if (filters.linkedOnly) params.append('linkedOnly', 'true');
    if (filters.userId) params.append('userId', filters.userId);
    const qs = params.toString();
    return fetchApi<DoorAccessEventsListResponse>(`${INTEGRATION_PATH}/events${qs ? `?${qs}` : ''}`);
  },
};

/** Somebody the door log can be filtered to. */
export type DoorPerson = { id: string; name: string; role: string };
